const scopes = new WeakMap();
const panels = '[role="menu"], [role="listbox"], [data-admin2026-check-ins-tools-panel], details > div';

// Opt in at the owning surface. Keep menus in their original tree so Headless UI,
// delegated actions, inherited theme, and form values retain their existing owners.
export const admin2026FloatingMenus = {
    mounted(scope) {
        const active = new Map();
        let frame = 0;
        const eligible = (panel) => !panel.closest('.admin2026-drawer-panel, .admin2026-drawer-surface, .admin2026-client-account-communication-rail-panel')
            && panel.dataset.admin2026ListboxOptionsPlacement !== 'flow'
            && (panel.parentElement.tagName !== 'DETAILS' || active.has(panel) || getComputedStyle(panel).position === 'absolute');
        const visible = (panel) => !panel.hidden && panel.isConnected && getComputedStyle(panel).display !== 'none'
            && (panel.parentElement.tagName !== 'DETAILS' || panel.parentElement.open);
        const restore = (panel, state) => {
            if (state.popover && panel.matches(':popover-open')) panel.hidePopover();
            if (state.popover) panel.removeAttribute('popover');
            state.styles.forEach(([node, style]) => {
                if (style === null) node.removeAttribute('style');
                else node.setAttribute('style', style);
            });
            panel.removeAttribute('data-admin2026-floating-menu');
        };
        const position = (panel, state) => {
            const anchor = state.opener.getBoundingClientRect();
            const viewport = window.visualViewport;
            const surface = scope.getBoundingClientRect();
            const leftEdge = Math.max((viewport?.offsetLeft || 0) + 12, surface.left + 12);
            const topEdge = (viewport?.offsetTop || 0) + 12;
            let rightEdge = Math.min((viewport?.offsetLeft || 0) + (viewport?.width || innerWidth) - 12, surface.right - 12);
            let bottomEdge = topEdge + (viewport?.height || innerHeight) - 24;
            const rail = document.querySelector('.admin2026-client-account-communication-rail-panel');
            if (innerWidth >= 768 && rail) rightEdge = Math.min(rightEdge, rail.getBoundingClientRect().left - 12);
            const navigation = document.querySelector('[data-admin2026-mobile-bottom-navigation]');
            if (navigation?.getBoundingClientRect().height) bottomEdge = Math.min(bottomEdge, navigation.getBoundingClientRect().top - 12);
            const sheet = panel.classList.contains('admin2026-mobile-action-sheet') && innerWidth < 1024;
            const width = Math.max(0, Math.min(sheet ? rightEdge - leftEdge : Math.max(state.width, anchor.width), rightEdge - leftEdge));
            const availableHeight = Math.max(0, bottomEdge - topEdge);
            const heightLimit = sheet ? Math.min(availableHeight, (viewport?.height || innerHeight) * .72) : Math.min(availableHeight, state.maxHeight);
            const style = panel.style;
            const set = (name, value) => style.setProperty(name, value, 'important');
            set('position', 'fixed');
            set('margin', '0');
            set('inset', 'auto');
            set('width', `${width}px`);
            set('min-width', '0');
            set('max-width', `${width}px`);
            set('max-height', `${heightLimit}px`);
            set('overflow-y', 'auto');
            set('box-sizing', 'border-box');
            set('padding', state.padding);
            if (state.centered) set('transform', 'none');
            state.scrollBodies.forEach(([body, limit]) => body.style.setProperty('max-height', `${Math.max(0, Math.min(limit, heightLimit - state.paddingHeight - 2))}px`, 'important'));
            const height = panel.offsetHeight;
            const below = anchor.bottom + state.gap;
            const above = anchor.top - state.gap - height;
            const top = sheet ? bottomEdge - height : below + height <= bottomEdge ? below
                : above >= topEdge ? above : Math.max(topEdge, bottomEdge - height);
            const left = state.centered ? anchor.left + (anchor.width - width) / 2 : state.alignRight ? anchor.right - width : anchor.left;
            set('left', `${Math.max(leftEdge, Math.min(left, rightEdge - width))}px`);
            set('top', `${top}px`);
        };
        const refresh = () => {
            frame = 0;
            for (const [panel, state] of active) {
                if (!scope.contains(panel) || !visible(panel)) {
                    observer.unobserve(panel);
                    restore(panel, state);
                    active.delete(panel);
                }
            }
            scope.querySelectorAll(panels).forEach(panel => {
                if (!eligible(panel) || !visible(panel)) return;
                let state = active.get(panel);
                if (!state) {
                    const opener = [...scope.querySelectorAll('button[aria-controls]')].find(button => button.getAttribute('aria-controls') === panel.id)
                        || (panel.getAttribute('aria-labelledby') || '').split(' ').map(id => document.getElementById(id)).find(node => node?.tagName === 'BUTTON' && scope.contains(node))
                        || (panel.parentElement.tagName === 'DETAILS' ? panel.parentElement.querySelector('summary') : null);
                    if (!opener) return;
                    const computed = getComputedStyle(panel);
                    const scrollBodies = [...panel.children].filter(body => ['auto', 'scroll'].includes(getComputedStyle(body).overflowY))
                        .map(body => [body, Number.parseFloat(getComputedStyle(body).maxHeight) || Infinity]);
                    state = {
                        opener, width: panel.offsetWidth, maxHeight: Number.parseFloat(computed.maxHeight) || Infinity,
                        gap: Number.parseFloat(computed.marginTop) || 8,
                        alignRight: computed.right !== 'auto' || panel.dataset.admin2026ListboxOptionsAlignment === 'right',
                        centered: panel.style.transform.includes('translateX(-50%)'),
                        padding: computed.padding, paddingHeight: parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom),
                        scrollBodies, styles: [panel, ...scrollBodies.map(([body]) => body)].map(node => [node, node.getAttribute('style')]),
                        popover: typeof panel.showPopover === 'function' && !panel.hasAttribute('popover'),
                    };
                    active.set(panel, state);
                    panel.dataset.admin2026FloatingMenu = 'true';
                    if (state.popover) {
                        panel.setAttribute('popover', 'manual');
                        panel.showPopover();
                    }
                    observer.observe(panel);
                }
                position(panel, state);
            });
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(refresh);
        };
        const observer = new ResizeObserver(schedule);
        observer.observe(scope);
        const mutations = new MutationObserver(schedule);
        mutations.observe(scope, {childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open']});
        window.addEventListener('resize', schedule);
        document.addEventListener('scroll', schedule, true);
        window.visualViewport?.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('scroll', schedule);
        schedule();
        scopes.set(scope, () => {
            mutations.disconnect();
            observer.disconnect();
            cancelAnimationFrame(frame);
            active.forEach((state, panel) => restore(panel, state));
            window.removeEventListener('resize', schedule);
            document.removeEventListener('scroll', schedule, true);
            window.visualViewport?.removeEventListener('resize', schedule);
            window.visualViewport?.removeEventListener('scroll', schedule);
        });
    },
    beforeUnmount(scope) {
        scopes.get(scope)?.();
        scopes.delete(scope);
    },
};
